/**
 * Template Generation Flow — 3-stage review, preview, generate.
 * From FRONTEND_BUILD_SPEC §3.7.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Download, FileOutput, Eye, ChevronLeft, Send, Loader2 } from "lucide-react";
import { api } from "../../lib/api.ts";
import { ProvenanceBadge } from "../shared/ProvenanceBadge.tsx";
import { AIInsight } from "../shared/AIInsight.tsx";
import { Button } from "../shared/Button.tsx";

interface TemplateGenerationFlowProps {
  projectId: string;
  templateId: string;
  countryCode: string;
  onBack: () => void;
}

type Stage = "review" | "preview" | "generated";

export function TemplateGenerationFlow({ projectId, templateId, countryCode, onBack }: TemplateGenerationFlowProps) {
  const navigate = useNavigate();
  const [completeness, setCompleteness] = useState<any>(null);
  const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("review");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.getTemplateCompleteness(projectId, templateId)
      .then((data) => {
        setCompleteness(data);
        // If a previous generation exists, show it directly
        if (data.last_generated?.html) {
          setGeneratedHtml(data.last_generated.html);
          setStage("generated");
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId, templateId]);

  async function handleGenerate() {
    setGenerating(true);
    try {
      const result = await api.generateTemplate(projectId, templateId);
      setGeneratedHtml(result.html);
      setCompleteness(result.completeness);
      setStage("generated");
    } catch (e) {
      console.error(e);
    } finally {
      setGenerating(false);
    }
  }

  if (loading) {
    return <div className="animate-pulse"><div className="h-5 bg-surface-tertiary rounded w-1/3 mb-4" /></div>;
  }

  if (!completeness) {
    return <div className="text-text-secondary text-[16px]">Template not found.</div>;
  }

  const stages = ["Review data", "Preview", "Generate"];

  return (
    <div>
      {/* Header */}
      <button onClick={onBack} className="flex items-center gap-1 text-[14px] text-text-secondary mb-4 hover:text-text-primary">
        <ChevronLeft size={14} /> Back to templates
      </button>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[18px] font-medium">{completeness.template_name}</h2>
          <p className="text-[13px] text-text-tertiary">{completeness.regulator} · {completeness.form_reference}</p>
        </div>
        <span className={`text-[13px] font-medium px-2 py-0.5 rounded-full ${
          completeness.can_generate_final ? "bg-brand-light text-brand" : "bg-warning-light text-warning-text"
        }`}>
          {completeness.completeness_pct}% ready
        </span>
      </div>

      {/* Stage indicator */}
      <div className="flex items-center gap-2 mb-5">
        {stages.map((s, i) => {
          const stageIdx = stage === "review" ? 0 : stage === "preview" ? 1 : 2;
          return (
            <div key={s} className="flex items-center gap-1.5">
              <div className={`w-5 h-5 rounded-full text-[11px] font-medium flex items-center justify-center ${
                i < stageIdx ? "bg-brand text-on-solid" : i === stageIdx ? "bg-brand text-on-solid" : "bg-surface-tertiary text-text-tertiary"
              }`}>
                {i < stageIdx ? <Check size={10} /> : i + 1}
              </div>
              <span className={`text-[13px] ${i === stageIdx ? "text-text-primary font-medium" : "text-text-tertiary"}`}>{s}</span>
              {i < stages.length - 1 && <span className="text-text-tertiary mx-1">→</span>}
            </div>
          );
        })}
      </div>

      {/* Stage 1: Review data */}
      {stage === "review" && (
        <div className="space-y-4">
          {/* Generating overlay */}
          {generating && (
            <div className="bg-surface border border-ai/20 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 size={20} className="text-ai animate-spin" />
                <div>
                  <p className="text-[15px] font-medium">Generating document...</p>
                  <p className="text-[13px] text-text-tertiary">Injecting field values and writing AI narrative sections</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[13px]">
                  <Check size={12} className="text-brand" />
                  <span className="text-text-secondary">Injecting {completeness.fields_filled} field values</span>
                </div>
                <div className="flex items-center gap-2 text-[13px]">
                  <Loader2 size={12} className="text-ai animate-spin" />
                  <span className="text-text-secondary">Writing {completeness.llm_sections.length} AI narrative sections...</span>
                </div>
                {completeness.required_docs.length > 0 && (
                  <div className="flex items-center gap-2 text-[13px]">
                    <div className="w-3 h-3 rounded-full border border-border" />
                    <span className="text-text-tertiary">Attaching {completeness.docs_uploaded} documents</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions — top position for easy access */}
          {!generating && (
            <>
              <div className="flex items-center gap-3">

                <Button
                  variant="secondary"
                  size="lg"
                  icon={Eye}
                  onClick={() => setStage("preview")}
                  disabled={!completeness.can_generate_draft}
                  disabledReason="no fields filled yet"
                >
                  Preview draft
                </Button>
                <button
                  onClick={handleGenerate}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[16px] font-medium transition-all cursor-pointer ${
                    completeness.can_generate_final
                      ? "bg-brand text-on-solid hover:brightness-110 hover:shadow-md"
                      : "bg-warning-light text-warning-text hover:brightness-95 hover:shadow-md"
                  }`}
                >
                  <FileOutput size={14} />
                  {completeness.can_generate_final ? "Generate final" :
                   `Generate draft — ${completeness.fields_total - completeness.fields_filled} fields missing`}
                </button>
              </div>

              <AIInsight
                text={completeness.can_generate_final
                  ? "All fields and documents are ready. You can generate the final submission version."
                  : `You can generate a draft with ${completeness.completeness_pct}% completeness — missing placeholders show as highlighted gaps. The final version requires all fields and attachments.`}
              />

              <div className="border-b border-border" />
            </>
          )}

          {/* Deterministic placeholders */}
          <div>
            <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
              Deterministic placeholders ({completeness.fields_filled}/{completeness.fields_total})
            </h3>
            <div className="space-y-1">
              {completeness.placeholders.map((p: any) => (
                <div key={p.field_id} className="flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-xl text-[14px]">
                  {p.status === "filled" ? (
                    <Check size={14} className="text-brand flex-shrink-0" />
                  ) : (
                    <X size={14} className="text-danger flex-shrink-0" />
                  )}
                  <span className="font-mono text-[11px] text-text-tertiary">{p.field_id}</span>
                  <span className="flex-1 truncate">{p.display_label}</span>
                  {p.status === "filled" ? (
                    <>
                      <span className="font-medium">{p.formatted_value}</span>
                      <ProvenanceBadge sourceMethod={p.source_method} />
                    </>
                  ) : (
                    <span className="text-danger text-[13px]">Missing</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* LLM narrative sections */}
          {completeness.llm_sections.length > 0 && (
            <div>
              <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
                AI narrative sections ({completeness.llm_sections.length})
              </h3>
              <div className="space-y-1">
                {completeness.llm_sections.map((section: string) => (
                  <div key={section} className="flex items-center gap-2 px-3 py-2 bg-ai-light/50 border border-ai/10 rounded-xl text-[14px]">
                    <div className="w-3.5 h-3.5 rounded-full bg-ai text-on-solid text-[8px] font-bold flex items-center justify-center">AI</div>
                    <span className="flex-1 capitalize">{section.replace(/_/g, " ")}</span>
                    <span className="text-[12px] text-ai">Will be generated</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document attachments */}
          {completeness.required_docs.length > 0 && (
            <div>
              <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
                Document attachments ({completeness.docs_uploaded}/{completeness.docs_total})
              </h3>
              <div className="space-y-1">
                {completeness.required_docs.map((doc: any) => (
                  <div key={doc.doc_type} className="flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-xl text-[14px]">
                    {doc.uploaded ? <Check size={14} className="text-brand" /> : <X size={14} className="text-danger" />}
                    <span className="flex-1 capitalize">{doc.doc_type.replace(/_/g, " ")}</span>
                    <span className={`text-[12px] ${doc.uploaded ? "text-brand" : "text-danger"}`}>
                      {doc.uploaded ? "Attached" : "Not uploaded"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stage 2: Preview — document-like structured layout */}
      {stage === "preview" && (
        <div>
          {/* Generating overlay */}
          {generating && (
            <div className="bg-surface border border-ai/20 rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 size={20} className="text-ai animate-spin" />
                <div>
                  <p className="text-[15px] font-medium">Generating document...</p>
                  <p className="text-[13px] text-text-tertiary">Injecting field values and writing AI narrative sections</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[13px]">
                  <Check size={12} className="text-brand" />
                  <span className="text-text-secondary">Injecting {completeness.fields_filled} field values</span>
                </div>
                <div className="flex items-center gap-2 text-[13px]">
                  <Loader2 size={12} className="text-ai animate-spin" />
                  <span className="text-text-secondary">Writing {completeness.llm_sections.length} AI narrative sections...</span>
                </div>
              </div>
            </div>
          )}

          {!generating && (
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setStage("review")} className="text-[14px] text-text-secondary">
                <ChevronLeft size={14} className="inline" /> Back to review
              </button>
              <span className="text-[13px] font-medium px-2 py-0.5 bg-warning-light text-warning-text rounded-full">
                Draft — not for submission
              </span>
              <Button size="lg" icon={FileOutput} className="ml-auto" onClick={handleGenerate}>
                {completeness.can_generate_final ? "Generate final" : "Generate draft"}
              </Button>
            </div>
          )}

          {/* Document preview */}
          <div className="bg-surface border border-border rounded-2xl overflow-hidden">
            {(() => {
              const sections = completeness.sections;
              const placeholderMap = new Map<string, any>(
                completeness.placeholders.map((p: any) => [p.field_id, p])
              );

              // Structured layout if sections are defined
              if (sections && sections.length > 0) {
                return (
                  <div className="divide-y divide-border">
                    {sections.map((section: any) => {
                      switch (section.type) {
                        case "cover":
                          return (
                            <div key={section.id} className="px-6 py-5">
                              <h3 className="text-[20px] font-semibold mb-1">{completeness.template_name}</h3>
                              {completeness.regulator && (
                                <p className="text-[14px] text-text-secondary">{completeness.regulator}</p>
                              )}
                              {completeness.form_reference && (
                                <p className="text-[13px] text-text-tertiary mt-0.5">{completeness.form_reference}</p>
                              )}
                            </div>
                          );

                        case "fields":
                          return (
                            <div key={section.id} className="px-6 py-4">
                              <h4 className="text-[15px] font-semibold text-text-primary mb-3">{section.title}</h4>
                              {section.sub_sections?.map((sub: any) => (
                                <div key={sub.id} className="mb-3 last:mb-0">
                                  <h5 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-1.5">{sub.title}</h5>
                                  <div className="space-y-1">
                                    {sub.field_ids?.map((fid: string) => {
                                      const p = placeholderMap.get(fid);
                                      if (!p) return null;
                                      return (
                                        <div key={fid} className="flex items-baseline gap-2 text-[14px] py-0.5">
                                          <span className="text-text-secondary min-w-[180px]">{p.display_label}:</span>
                                          {p.status === "filled" ? (
                                            <span className="text-brand font-medium">{p.formatted_value}</span>
                                          ) : (
                                            <span className="bg-warning-light px-1.5 py-0.5 rounded font-mono text-[12px] text-warning-text">{`{{${p.field_id}}}`}</span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );

                        case "narrative":
                          return (
                            <div key={section.id} className="px-6 py-4">
                              <h4 className="text-[15px] font-semibold text-text-primary mb-3">{section.title}</h4>
                              <div className="space-y-3">
                                {section.sub_sections?.map((sub: any) => (
                                  <div key={sub.id} className="border-l-2 border-ai/30 pl-3 py-2 bg-ai-light/20 rounded-r">
                                    <div className="flex items-center gap-1.5 mb-1">
                                      <div className="w-3.5 h-3.5 rounded-full bg-ai text-on-solid text-[8px] font-bold flex items-center justify-center">AI</div>
                                      <span className="text-[13px] font-medium">{sub.title}</span>
                                    </div>
                                    <p className="text-[13px] text-text-tertiary italic">
                                      Will be generated based on project data{sub.model_tier === "quality" ? " (enhanced quality)" : ""}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );

                        case "checklist":
                        case "attachments":
                          return (
                            <div key={section.id} className="px-6 py-4">
                              <h4 className="text-[15px] font-semibold text-text-primary mb-3">{section.title}</h4>
                              <div className="space-y-1">
                                {completeness.required_docs.map((doc: any) => (
                                  <div key={doc.doc_type} className="flex items-center gap-2 text-[14px] py-0.5">
                                    {doc.uploaded ? (
                                      <Check size={13} className="text-brand" />
                                    ) : (
                                      <X size={13} className="text-danger" />
                                    )}
                                    <span className="capitalize">{doc.doc_type.replace(/_/g, " ")}</span>
                                    <span className={`ml-auto text-[12px] ${doc.uploaded ? "text-brand" : "text-danger"}`}>
                                      {doc.uploaded ? "Attached" : "Not uploaded"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );

                        default:
                          return null;
                      }
                    })}
                  </div>
                );
              }

              // Fallback: flat layout for templates without sections
              return (
                <div className="p-5 space-y-3">
                  <h3 className="text-[18px] font-medium border-b border-border pb-2">{completeness.template_name}</h3>
                  {completeness.placeholders.map((p: any) => (
                    <div key={p.field_id} className="text-[14px]">
                      <strong>{p.display_label}: </strong>
                      {p.status === "filled" ? (
                        <span className="text-brand font-medium">{p.formatted_value}</span>
                      ) : (
                        <span className="bg-warning-light px-1 rounded font-mono text-[12px] text-warning-text">{`{{${p.field_id}}}`}</span>
                      )}
                    </div>
                  ))}
                  {completeness.llm_sections.map((section: string) => (
                    <div key={section} className="border-l-2 border-ai/30 pl-3 py-2 bg-ai-light/30 rounded-r">
                      <div className="text-[12px] text-ai font-medium mb-1">AI-generated</div>
                      <p className="text-[14px] text-text-secondary italic">
                        [{section.replace(/_/g, " ")} — will be generated by AI]
                      </p>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

        </div>
      )}

      {/* Stage 3: Generated */}
      {stage === "generated" && (
        <div className="space-y-4">
          <div className="bg-brand-light border border-brand/20 rounded-2xl p-4 flex items-center gap-3">
            <Check size={22} className="text-brand flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[15px] font-medium text-brand">
                {completeness.can_generate_final ? "Final document generated" : "Draft generated"}
                {completeness.last_generated?.created_at && (
                  <span className="font-normal text-brand/60"> · {new Date(completeness.last_generated.created_at).toLocaleDateString()}</span>
                )}
              </p>
              <p className="text-[13px] text-brand/60">
                {completeness.fields_filled} values injected · {completeness.last_generated ? `${completeness.last_generated.completeness_pct}% at generation` : `${completeness.completeness_pct}%`}
              </p>
            </div>
            <button
              onClick={() => { setStage("review"); }}
              className="flex items-center gap-1 px-3 py-1 bg-brand/10 text-brand rounded-lg text-[13px] font-medium hover:bg-brand/20"
            >
              <Eye size={12} /> Review & regenerate
            </button>
          </div>

          {/* Remaining steps (above document preview) */}
          {!completeness.can_generate_final && (
            <div className="bg-surface border border-border rounded-2xl p-4">
              <h4 className="text-[14px] font-medium mb-2">Remaining steps to final submission</h4>
              <div className="space-y-1.5 text-[14px]">
                {completeness.placeholders.filter((p: any) => p.status === "missing").map((p: any) => (
                  <div key={p.field_id} className="flex items-center gap-2 text-danger">
                    <X size={12} /> Fill {p.display_label} ({p.field_id})
                  </div>
                ))}
                {completeness.required_docs.filter((d: any) => !d.uploaded).map((d: any) => (
                  <div key={d.doc_type} className="flex items-center gap-2 text-danger">
                    <X size={12} /> Upload {d.doc_type.replace(/_/g, " ")}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next step: record submission (only if final) */}
          {completeness.can_generate_final && completeness.license_type && (
            <div className="bg-info-light/30 border border-info/20 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <Send size={18} className="text-info flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[15px] font-medium text-text-primary mb-1">Next: submit to the regulator</div>
                  <p className="text-[13px] text-text-secondary mb-3">
                    Download the document, submit it to the regulator, then record the submission here to track follow-up dates.
                  </p>
                  <button
                    onClick={() => navigate(`/projects/${projectId}/licenses/${completeness.license_type}?record=1`)}
                    className="px-3 py-1.5 bg-info text-on-solid rounded-lg text-[14px] font-medium hover:bg-info/90 transition-colors"
                  >
                    Record submission →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Document preview */}
          {generatedHtml && (
            <div className="bg-surface border border-border rounded-2xl overflow-hidden animate-fade-in-down">
              <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-surface-tertiary">
                <span className="text-[13px] font-medium text-text-secondary">Document Preview</span>
                <Button size="sm" icon={Download} onClick={() => api.downloadTemplate(projectId, templateId)}>
                  Download .docx
                </Button>
              </div>
              <div
                className="p-6 template-preview-content text-[14px] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: generatedHtml }}
              />
            </div>
          )}

          {/* Download card (fallback if no preview HTML) */}
          {!generatedHtml && (
            <div className="flex items-center gap-3 px-4 py-3 bg-surface border border-border rounded-2xl">
              <FileOutput size={20} className="text-brand" />
              <div className="flex-1">
                <div className="text-[16px] font-medium">
                  {completeness.template_id}_{completeness.template_name.replace(/\s+/g, "_")}_{completeness.can_generate_final ? "FINAL" : "DRAFT"}.docx
                </div>
                <div className="text-[13px] text-text-tertiary">
                  Generated {new Date().toLocaleString()} · Template v{1}
                </div>
              </div>
              <Button icon={Download} onClick={() => api.downloadTemplate(projectId, templateId)}>
                Download .docx
              </Button>
            </div>
          )}

          <AIInsight
            text={completeness.can_generate_final
              ? "This document is ready for submission. Review it carefully before sending to the regulator."
              : "Upload remaining documents or fill missing fields to unlock the final version."}
          />
        </div>
      )}
    </div>
  );
}
